import React, { useEffect, useMemo, useState } from 'react';
import { listVectorTools } from '../utils/api';

/**
 * Editor for tool_chain steps: add/remove/reorder + interactive flags + failure policy.
 */
export default function ToolChainStepsEditor({
  steps = [],
  failurePolicy = 'stop',
  onChangeSteps,
  onChangeFailurePolicy,
  disabled = false,
  excludeToolId = null,
  excludeToolSlug = null,
  error = null,
}) {
  const [available, setAvailable] = useState([]);
  const [loading, setLoading] = useState(false);
  const [loadError, setLoadError] = useState('');
  const [pickSlug, setPickSlug] = useState('');

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setLoadError('');
      try {
        const all = [];
        let page = 1;
        let totalPages = 1;
        do {
          const res = await listVectorTools(page, 100);
          const items = res?.items || res?.data?.items || [];
          all.push(...items);
          totalPages = res?.totalPages || res?.data?.totalPages || 1;
          page += 1;
        } while (page <= totalPages && page <= 20);
        if (!cancelled) setAvailable(all);
      } catch (err) {
        if (!cancelled) setLoadError(err.message || 'Failed to load tools');
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const options = useMemo(() => {
    const excludeId = excludeToolId ? String(excludeToolId).toLowerCase() : '';
    const excludeSlug = excludeToolSlug ? String(excludeToolSlug).toLowerCase() : '';
    return (available || [])
      .filter((t) => {
        if (excludeId && String(t.id || '').toLowerCase() === excludeId) return false;
        if (excludeSlug && String(t.slug || '').toLowerCase() === excludeSlug) return false;
        return true;
      })
      .slice()
      .sort((a, b) => String(a.name || a.slug || '').localeCompare(String(b.name || b.slug || '')));
  }, [available, excludeToolId, excludeToolSlug]);

  const labelFor = (step) => {
    const match = options.find(
      (t) =>
        (step.tool_id && String(t.id) === String(step.tool_id))
        || (step.tool_slug && String(t.slug) === String(step.tool_slug))
    );
    if (match) {
      const kind = match.tool_kind || match.kind || 'search';
      return `${match.name || match.slug} (${kind})`;
    }
    return step.tool_slug || step.tool_id || 'Unknown tool';
  };

  const move = (index, delta) => {
    const next = [...steps];
    const j = index + delta;
    if (j < 0 || j >= next.length) return;
    const tmp = next[index];
    next[index] = next[j];
    next[j] = tmp;
    onChangeSteps?.(next);
  };

  const removeAt = (index) => {
    onChangeSteps?.(steps.filter((_, i) => i !== index));
  };

  const toggleInteractive = (index) => {
    onChangeSteps?.(
      steps.map((s, i) => (i === index ? { ...s, interactive: !s.interactive } : s))
    );
  };

  const stepArgsText = (step) => {
    if (typeof step.args === 'string') return step.args;
    if (step.args && typeof step.args === 'object') {
      try {
        return JSON.stringify(step.args, null, 2);
      } catch (_) {
        return '';
      }
    }
    return '';
  };

  const setStepArgs = (index, text) => {
    onChangeSteps?.(
      steps.map((s, i) => {
        if (i !== index) return s;
        const trimmed = String(text || '').trim();
        if (!trimmed) {
          const next = { ...s };
          delete next.args;
          return next;
        }
        try {
          const parsed = JSON.parse(trimmed);
          if (parsed && typeof parsed === 'object' && !Array.isArray(parsed)) {
            return { ...s, args: parsed };
          }
        } catch (_) {
          /* keep raw text so the user can finish editing; save validation rejects invalid JSON */
        }
        return { ...s, args: trimmed };
      })
    );
  };

  const addStep = () => {
    if (!pickSlug) return;
    const tool = options.find((t) => String(t.slug) === String(pickSlug));
    if (!tool) return;
    onChangeSteps?.([
      ...steps,
      {
        tool_id: tool.id || null,
        tool_slug: tool.slug,
        interactive: false,
      },
    ]);
    setPickSlug('');
  };

  return (
    <div className="tool-chain-steps-editor">
      <div className="form-group">
        <label>On step failure</label>
        <div style={{ display: 'flex', gap: 12, flexWrap: 'wrap' }}>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="radio"
              name="failure_policy"
              checked={failurePolicy === 'stop'}
              disabled={disabled}
              onChange={() => onChangeFailurePolicy?.('stop')}
            />
            Stop on failure
          </label>
          <label style={{ display: 'inline-flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
            <input
              type="radio"
              name="failure_policy"
              checked={failurePolicy === 'continue'}
              disabled={disabled}
              onChange={() => onChangeFailurePolicy?.('continue')}
            />
            Continue on failure
          </label>
        </div>
      </div>

      <div className="form-group">
        <label>Chain steps <span className="required">*</span></label>
        {loading && <small className="form-help">Loading available tools…</small>}
        {loadError && <small className="form-error">{loadError}</small>}
        {error && <small className="form-error">{error}</small>}
        {steps.length === 0 && !loading && (
          <small className="form-help">Add at least one tool. Order is top → bottom.</small>
        )}
        <ul style={{ listStyle: 'none', padding: 0, margin: '8px 0' }}>
          {steps.map((step, index) => (
            <li
              key={`${step.tool_id || step.tool_slug}-${index}`}
              style={{
                display: 'flex',
                flexDirection: 'column',
                gap: 8,
                padding: '8px 10px',
                marginBottom: 6,
                border: '1px solid var(--border-color, #d1d5db)',
                borderRadius: 8,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 8, flexWrap: 'wrap' }}>
                <span style={{ fontWeight: 600, minWidth: 28 }}>{index + 1}.</span>
                <span style={{ flex: 1, minWidth: 160 }}>{labelFor(step)}</span>
                <label style={{ display: 'inline-flex', alignItems: 'center', gap: 4, fontSize: '0.9rem' }}>
                  <input
                    type="checkbox"
                    checked={!!step.interactive}
                    disabled={disabled}
                    onChange={() => toggleInteractive(index)}
                  />
                  Interactive
                </label>
                <button type="button" className="btn btn-sm ghost" disabled={disabled || index === 0} onClick={() => move(index, -1)} title="Move up">
                  <i className="fas fa-arrow-up" />
                </button>
                <button type="button" className="btn btn-sm ghost" disabled={disabled || index === steps.length - 1} onClick={() => move(index, 1)} title="Move down">
                  <i className="fas fa-arrow-down" />
                </button>
                <button type="button" className="btn btn-sm ghost" disabled={disabled} onClick={() => removeAt(index)} title="Remove">
                  <i className="fas fa-trash" />
                </button>
              </div>
              <textarea
                className="form-control"
                rows={3}
                disabled={disabled}
                value={stepArgsText(step)}
                onChange={(e) => setStepArgs(index, e.target.value)}
                placeholder='Optional step args JSON, e.g. {"filters":[{"key":"status","op":"eq","value":"New"}],"order_by":[{"key":"created_at","dir":"asc"}],"limit":1}'
              />
            </li>
          ))}
        </ul>

        <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', alignItems: 'center' }}>
          <select
            className="form-control"
            value={pickSlug}
            disabled={disabled || loading}
            onChange={(e) => setPickSlug(e.target.value)}
            style={{ minWidth: 220, flex: 1 }}
          >
            <option value="">Select a tool to add…</option>
            {options.map((t) => (
              <option key={t.id || t.slug} value={t.slug}>
                {(t.name || t.slug)} ({t.tool_kind || t.kind || 'search'})
              </option>
            ))}
          </select>
          <button type="button" className="btn btn-sm primary" disabled={disabled || !pickSlug} onClick={addStep}>
            <i className="fas fa-plus" /> Add step
          </button>
        </div>
        <small className="form-help">
          Optional JSON on each step is passed as that tool’s arguments. Filter steps need a
          <code>filters</code> array here or on the filter tool’s default arguments — scheduled runs
          do not send chat/LLM args.
        </small>
      </div>
    </div>
  );
}
